import { useEffect, useState } from 'react';
import { parseRoles, type Role } from '@unit/shared';
import { useAuth } from './AuthProvider.js';

interface ClaimRoles {
  roles: Role[];
  /** True until the ID token has been read, so guards do not flash "forbidden". */
  loading: boolean;
}

/**
 * The caller's roles, read from the ID token's custom claims.
 *
 * For rendering only. Every server route re-reads the roles from the token it
 * verifies itself, and Firestore rules read them from the same claims — a UI
 * that hid the wrong button would be a cosmetic bug, never a security one.
 */
export function useClaimRoles(): ClaimRoles {
  const { user } = useAuth();
  const [state, setState] = useState<ClaimRoles>({ roles: [], loading: true });

  useEffect(() => {
    if (!user) {
      setState({ roles: [], loading: false });
      return;
    }

    let cancelled = false;
    setState({ roles: [], loading: true });

    user
      .getIdTokenResult()
      .then((result) => {
        if (!cancelled) setState({ roles: parseRoles(result.claims.roles), loading: false });
      })
      .catch(() => {
        // A token that cannot be read is a sign-in problem, not a role problem.
        // Surfacing zero roles keeps the UI at its least privileged.
        if (!cancelled) setState({ roles: [], loading: false });
      });

    return () => {
      cancelled = true;
    };
  }, [user]);

  return state;
}
